import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getCourses } from '../../store/courseSlice';
import { getLessons } from '../../store/lessonSlice';
import { getPrograms } from '../../store/programSlice';
import BreadcrumbNav from './BreadcrumbNav';

export default function LessonDetails() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { lessonId } = useParams();
    const [lesson, setLesson] = useState(null);
    const [course, setCourse] = useState(null);
    const [program, setProgram] = useState(null);
    
    const allLessons = useSelector(state => state.lesson.list);
    const courses = useSelector(state => state.course.list);
    const programs = useSelector(state => state.program.list);

    useEffect(() => {
        dispatch(getCourses());
        dispatch(getPrograms());
        // Load all lessons to ensure we have the lesson data
        dispatch(getLessons());
    }, [dispatch]);

    useEffect(() => {
        const currentLesson = allLessons.find(l => l.id === parseInt(lessonId));
        setLesson(currentLesson);
        
        if (currentLesson) {
            const currentCourse = courses.find(c => c.id === currentLesson.course_id);
            setCourse(currentCourse);
            
            if (currentCourse) {
                const currentProgram = programs.find(p => p.id === currentCourse.program_id);
                setProgram(currentProgram);
            }
        }
    }, [allLessons, courses, programs, lessonId]);

    const handleEdit = () => {
        if (lesson) {
            navigate(`/curriculum/lessons/${lesson.id}`);
        }
    };



    if (!lesson || !course || !program) {
        return <div className="container-fluid p-0">Loading...</div>;
    }

    return (
        <div className="container-fluid p-0">
            <BreadcrumbNav 
                items={[
                    { label: 'Curriculum', href: '/curriculum' },
                    { label: program.name, href: `/curriculum/programs/${program.id}` },
                    { label: course.name, href: `/curriculum/courses/${course.id}` },
                    { label: lesson.name, href: null }
                ]}
            />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="h3 mb-0">{lesson.name}</h1>
                </div>
                <button className="btn btn-primary" onClick={handleEdit}>
                    Edit Lesson
                </button>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <h5>Lesson Information</h5>
                                    <table className="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <td><strong>Name:</strong></td>
                                                <td>{lesson.name}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Difficulty:</strong></td>
                                                <td>
                                                    <span className="badge badge-primary">{lesson.difficulty}</span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Status:</strong></td>
                                                <td>
                                                    <span className={`badge badge-${lesson.status === 'active' ? 'success' : 'secondary'}`}>
                                                        {lesson.status}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Featured:</strong></td>
                                                <td>
                                                    <span className={`badge badge-${lesson.featured ? 'warning' : 'secondary'}`}>
                                                        {lesson.featured ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-md-6">
                                    <h5>Course & Program</h5>
                                    <table className="table table-borderless">
                                        <tbody>
                                            <tr>
                                                <td><strong>Course:</strong></td>
                                                <td>{course.name}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Program:</strong></td>
                                                <td>{program.name}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Course Difficulty:</strong></td>
                                                <td>
                                                    <span className="badge badge-primary">{course.difficulty}</span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Program Difficulty:</strong></td>
                                                <td>
                                                    <span className="badge badge-primary">{program.difficulty}</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h5>Lesson Content</h5>
                                <div className="card">
                                    <div className="card-body">
                                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                                            {lesson.content || 'No content available'}
                                        </pre>
                                    </div>
                                </div>
                            </div>

                            {lesson.content_ext && (
                                <div className="mb-4">
                                    <h5>Extended Content</h5>
                                    <div className="card">
                                        <div className="card-body">
                                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                                                {lesson.content_ext}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {lesson.image && (
                                <div className="mb-4">
                                    <h5>Lesson Image</h5>
                                    <div className="card">
                                        <div className="card-body">
                                            <img 
                                                src={lesson.image} 
                                                alt={lesson.name}
                                                className="img-fluid"
                                                style={{ maxHeight: '300px' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
} 